import type { PostHog } from "posthog-js";

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const enabled = Boolean(key) && import.meta.env.MODE !== "test";
let client: PostHog | undefined;
let failed = false;
const pending: Array<(instance: PostHog) => void> = [];
const consumedSensitiveParameters = new Map<string, string>();

const SENSITIVE_URL_PARAMETERS = new Set([
  "access_code",
  "bootstrap",
  "code",
  "operation",
  "state",
  "token",
]);

const SAFE_EVENT_PROPERTIES: Record<string, ReadonlySet<string>> = {
  agent_setup_approved: new Set(["visibility"]),
  app_publish_failed: new Set(["is_republish"]),
  app_published: new Set(["file_count", "is_republish"]),
  folder_chosen: new Set(["ai_status", "file_count", "folder_kind", "omitted_count"]),
  guide_publish_clicked: new Set(["location"]),
  plan_upgrade_started: new Set(["billing_quantity", "cadence", "plan"]),
  sign_in_started: new Set(["provider"]),
};

export const POSTHOG_PRIVACY_OPTIONS = Object.freeze({
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  save_campaign_params: false,
  save_referrer: false,
  mask_personal_data_properties: true,
  property_denylist: ["$current_url", "$referrer", "$initial_referrer"],
});

export function consumeSensitiveUrlParameters(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  let changed = false;
  for (const name of SENSITIVE_URL_PARAMETERS) {
    const value = url.searchParams.get(name);
    if (value === null) continue;
    consumedSensitiveParameters.set(name, value);
    url.searchParams.delete(name);
    changed = true;
  }
  if (changed) {
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }
}

export function consumedSensitiveUrlParameter(name: string): string | null {
  return consumedSensitiveParameters.get(name) ?? null;
}

export function sanitizePostHogProperties(
  event: string,
  properties?: Record<string, unknown>,
): Record<string, string | number | boolean> | undefined {
  if (!properties) return undefined;
  const permitted = SAFE_EVENT_PROPERTIES[event];
  if (!permitted) return undefined;
  const sanitized: Record<string, string | number | boolean> = {};
  for (const [name, value] of Object.entries(properties)) {
    if (!permitted.has(name)) continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      sanitized[name] = value;
    }
  }
  return sanitized;
}

consumeSensitiveUrlParameters();

if (enabled && key) {
  void import("posthog-js").then(({ default: posthogClient }) => {
    const initialized = posthogClient.init(key, {
      api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://us.i.posthog.com",
      defaults: "2026-05-30",
      person_profiles: "identified_only",
      ...POSTHOG_PRIVACY_OPTIONS,
    });
    client = initialized;
    for (const operation of pending.splice(0)) operation(initialized);
  }).catch(() => {
    failed = true;
    pending.splice(0);
  });
}

function withClient(operation: (instance: PostHog) => void): void {
  if (!enabled || failed) return;
  if (client) operation(client);
  else pending.push(operation);
}

export const posthog = {
  capture(event: string, properties?: Record<string, unknown>): void {
    withClient((instance) => { instance.capture(event, sanitizePostHogProperties(event, properties)); });
  },
  identify(distinctId: string): void {
    withClient((instance) => { instance.identify(distinctId); });
  },
  reset(): void {
    withClient((instance) => { instance.reset(); });
  },
};
